import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const OperationConfigPanel = ({ 
  selectedTemplate, 
  configuration, 
  onConfigChange, 
  onExecute, 
  onSchedule,
  isExecuting,
  progress 
}) => {
  if (!selectedTemplate) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-4">
          <Icon name="Settings" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="font-semibold text-[var(--color-foreground)] mb-2">
          Keine Operation ausgewählt
        </h3>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Wählen Sie eine Vorlage aus, um zu beginnen
        </p>
      </div>
    );
  }

  const priorityOptions = [
    { value: 'low', label: 'Niedrig' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'Hoch' }
  ];

  const executionModeOptions = [
    { value: 'immediate', label: 'Sofort ausführen' },
    { value: 'scheduled', label: 'Geplant' },
    { value: 'off-peak', label: 'Außerhalb der Spitzenzeiten' }
  ];

  return (
    <div className="h-full flex flex-col bg-[var(--color-card)] rounded-lg border border-[var(--color-border)]">
      <div className="p-4 border-b border-[var(--color-border)]">
        <h3 className="font-semibold text-[var(--color-foreground)] mb-1">
          Konfiguration
        </h3>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {selectedTemplate?.name}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <Select
            label="Ausführungsmodus"
            options={executionModeOptions}
            value={configuration?.executionMode}
            onChange={(value) => onConfigChange('executionMode', value)}
          />
        </div>

        {configuration?.executionMode === 'scheduled' && (
          <div>
            <Input
              type="datetime-local"
              label="Geplante Zeit"
              value={configuration?.scheduledTime}
              onChange={(e) => onConfigChange('scheduledTime', e?.target?.value)}
            />
          </div>
        )}

        <div>
          <Select
            label="Priorität"
            options={priorityOptions}
            value={configuration?.priority}
            onChange={(value) => onConfigChange('priority', value)}
          />
        </div>

        <div>
          <Input
            type="number"
            label="Batch-Größe"
            description="Anzahl der gleichzeitig zu verarbeitenden Datensätze"
            value={configuration?.batchSize}
            onChange={(e) => onConfigChange('batchSize', e?.target?.value)}
            min="1"
            max="1000"
          />
        </div>

        <div>
          <Checkbox
            label="Rollback bei Fehler aktivieren"
            description="Änderungen automatisch rückgängig machen, wenn Fehler auftreten"
            checked={configuration?.enableRollback}
            onChange={(e) => onConfigChange('enableRollback', e?.target?.checked)}
          />
        </div>

        <div>
          <Checkbox
            label="E-Mail-Benachrichtigung senden"
            description="Benachrichtigung nach Abschluss der Operation senden"
            checked={configuration?.sendNotification}
            onChange={(e) => onConfigChange('sendNotification', e?.target?.checked)}
          />
        </div>

        {selectedTemplate?.requiresApproval && (
          <div className="p-3 rounded-lg bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/20">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[var(--color-warning)] mb-1">
                  Genehmigung erforderlich
                </p>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Diese Operation erfordert eine Genehmigung durch einen Vorgesetzten
                </p>
              </div>
            </div>
          </div>
        )}

        {isExecuting && progress && (
          <div className="space-y-3">
            <div className="p-4 rounded-lg bg-[var(--color-primary)]/5 border border-[var(--color-primary)]/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">
                  Fortschritt
                </span>
                <span className="text-sm font-semibold text-[var(--color-primary)]">
                  {progress?.percentage}%
                </span>
              </div>
              <div className="w-full h-2 bg-[var(--color-muted)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--color-primary)] transition-all duration-300"
                  style={{ width: `${progress?.percentage}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-[var(--color-muted-foreground)]">
                <span>{progress?.processed} von {progress?.total} verarbeitet</span>
                <span>{progress?.estimatedTime}</span>
              </div>
            </div>

            {progress?.errors > 0 && (
              <div className="p-3 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/20">
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                  <span className="text-sm text-[var(--color-error)]">
                    {progress?.errors} Fehler aufgetreten
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="p-4 border-t border-[var(--color-border)] space-y-2">
        <Button
          variant="default"
          fullWidth
          iconName="Play"
          iconPosition="left"
          onClick={onExecute}
          disabled={isExecuting}
          loading={isExecuting}
        >
          {isExecuting ? 'Wird ausgeführt...' : 'Operation ausführen'}
        </Button>
        {configuration?.executionMode === 'immediate' && (
          <Button
            variant="outline"
            fullWidth
            iconName="Clock"
            iconPosition="left"
            onClick={onSchedule}
            disabled={isExecuting}
          >
            Für später planen
          </Button>
        )}
      </div>
    </div>
  );
};

export default OperationConfigPanel;